import React, { useEffect, useMemo, useRef, useState } from "react";

const READY_MESSAGE = "workflow86-artifact-app-ready";
const RESIZE_MESSAGE = "workflow86-artifact-app-resize";
const SUBMIT_MESSAGE = "workflow86-artifact-app-submit";
const ERROR_MESSAGE = "workflow86-artifact-app-error";

function createFrameId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function scriptStringLiteral(value = "") {
  return JSON.stringify(String(value))
    .replaceAll("<", "\\u003c")
    .replaceAll(">", "\\u003e")
    .replaceAll("&", "\\u0026")
    .replaceAll("\u2028", "\\u2028")
    .replaceAll("\u2029", "\\u2029");
}

export function buildSrcDoc({
  frameId,
  compiledJs,
  artifactContext,
  disabled,
}) {
  const compiledBundleLiteral = scriptStringLiteral(compiledJs || "");
  const artifactContextLiteral = scriptStringLiteral(
    JSON.stringify(artifactContext || {})
  );
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data:; font-src 'none'; connect-src 'none'; media-src 'none'; object-src 'none'; base-uri 'none'; form-action 'none';" />
    <style>
      html, body { margin: 0; min-height: 100%; }
      body { box-sizing: border-box; font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
      *, *::before, *::after { box-sizing: border-box; }
    </style>
  </head>
  <body>
    <div id="artifact-root"></div>
    <script>
      (() => {
        const frameId = ${JSON.stringify(frameId)};
        const disabled = ${disabled ? "true" : "false"};
        const artifactContext = JSON.parse(${artifactContextLiteral});
        const READY_MESSAGE = ${JSON.stringify(READY_MESSAGE)};
        const RESIZE_MESSAGE = ${JSON.stringify(RESIZE_MESSAGE)};
        const SUBMIT_MESSAGE = ${JSON.stringify(SUBMIT_MESSAGE)};
        const ERROR_MESSAGE = ${JSON.stringify(ERROR_MESSAGE)};
        const compiledBundle = ${compiledBundleLiteral};
        let renderCallback = null;
        window.__workflow86ArtifactCompiledBundle = compiledBundle;

        console.debug('[ArtifactAppFrame] iframe bootstrap', {
          frameId,
          disabled,
          compiledBundleLength: compiledBundle.length,
          artifactContext
        });

        function post(message) {
          parent.postMessage({ ...message, frameId }, '*');
        }

        function postHeight() {
          const body = document.body;
          const doc = document.documentElement;
          post({
            type: RESIZE_MESSAGE,
            height: Math.max(body.scrollHeight, body.offsetHeight, doc.scrollHeight, doc.offsetHeight)
          });
        }

        function reportError(message, metadata) {
          console.error('[ArtifactAppFrame] runtime error', message, metadata || {});
          post({
            type: ERROR_MESSAGE,
            message: String(message || 'Artifact app runtime error.'),
            details: metadata || {}
          });
        }

        window.__workflow86ArtifactReportError = reportError;

        window.artifact = {
          render(callback) {
            console.debug('[ArtifactAppFrame] artifact.render registered', {
              callbackType: typeof callback
            });
            renderCallback = callback;
          }
        };

        window.addEventListener('error', event => {
          reportError(event.message, {
            line: event.lineno,
            column: event.colno
          });
        });

        window.addEventListener('unhandledrejection', event => {
          reportError(event.reason && event.reason.message ? event.reason.message : event.reason);
        });

        window.__workflow86ArtifactMount = () => {
          const root = document.getElementById('artifact-root');
          console.debug('[ArtifactAppFrame] mount requested', {
            hasRoot: !!root,
            renderCallbackType: typeof renderCallback
          });
          if (typeof renderCallback !== 'function') {
            reportError('TypeScript artifact did not call artifact.render.');
            return;
          }
          const runtime = {
            context: artifactContext,
            submit(eventName, payload) {
              if (disabled) return;
              post({
                type: SUBMIT_MESSAGE,
                eventName: String(eventName || '').trim(),
                payload: payload && typeof payload === 'object' ? payload : {}
              });
            },
            resize: postHeight,
            reportError
          };
          try {
            renderCallback(root, runtime);
            console.debug('[ArtifactAppFrame] render callback completed');
            post({ type: READY_MESSAGE });
            postHeight();
          } catch (error) {
            reportError(error && error.message ? error.message : error);
          }
          if (typeof ResizeObserver !== 'undefined') {
            new ResizeObserver(postHeight).observe(document.body);
          }
          if (typeof MutationObserver !== 'undefined') {
            new MutationObserver(postHeight).observe(document.body, {
              attributes: true,
              childList: true,
              subtree: true
            });
          }
          setTimeout(postHeight, 0);
        };
      })();
    </script>
    <script>
      (() => {
        try {
          console.debug('[ArtifactAppFrame] injecting compiled bundle', {
            compiledBundleLength: window.__workflow86ArtifactCompiledBundle.length,
            compiledBundlePreview: window.__workflow86ArtifactCompiledBundle.slice(0, 160)
          });
          const bundleScript = document.createElement('script');
          bundleScript.text = window.__workflow86ArtifactCompiledBundle;
          document.body.appendChild(bundleScript);
          console.debug('[ArtifactAppFrame] compiled bundle injected');
        } catch (error) {
          window.__workflow86ArtifactReportError && window.__workflow86ArtifactReportError(error && error.message ? error.message : error);
        }
        window.__workflow86ArtifactMount && window.__workflow86ArtifactMount();
      })();
    </script>
  </body>
</html>`;
}

export default function ArtifactAppFrame({
  compiledJs,
  artifactContext,
  disabled = false,
  onSubmitEvent,
  className,
  title = "Artifact",
  minHeight = 240,
  style,
}) {
  const iframeRef = useRef(null);
  const frameId = useMemo(createFrameId, []);
  const [height, setHeight] = useState(minHeight);
  const [runtimeError, setRuntimeError] = useState("");
  const srcDoc = useMemo(
    () => buildSrcDoc({ frameId, compiledJs, artifactContext, disabled }),
    [artifactContext, compiledJs, disabled, frameId]
  );

  useEffect(() => {
    function handleMessage(event) {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const message = event.data || {};
      if (message.frameId !== frameId) return;
      if (message.type === RESIZE_MESSAGE && Number.isFinite(message.height)) {
        setHeight(Math.max(minHeight, Math.ceil(message.height)));
      }
      if (message.type === READY_MESSAGE) {
        console.debug("[ArtifactAppFrame] ready message received", {
          frameId,
          artifactContext,
        });
        setRuntimeError("");
      }
      if (message.type === SUBMIT_MESSAGE && !disabled) {
        console.debug("[ArtifactAppFrame] submit message received", {
          frameId,
          eventName: message.eventName,
          payload: message.payload,
        });
        onSubmitEvent?.(message.eventName || "", message.payload || {});
      }
      if (message.type === ERROR_MESSAGE) {
        console.error("[ArtifactAppFrame] error message received", {
          frameId,
          message: message.message,
          details: message.details,
          compiledJsLength: compiledJs?.length || 0,
          compiledJsPreview: String(compiledJs || "").slice(0, 160),
        });
        setRuntimeError(message.message || "Artifact app runtime error.");
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [
    artifactContext,
    compiledJs,
    disabled,
    frameId,
    minHeight,
    onSubmitEvent,
  ]);

  return (
    <div className={className} style={style}>
      {runtimeError ? (
        <div role="alert" style={{ color: "#b00020", padding: 8 }}>
          {runtimeError}
        </div>
      ) : null}
      <iframe
        ref={iframeRef}
        title={title}
        srcDoc={srcDoc}
        sandbox="allow-scripts"
        style={{
          border: 0,
          display: "block",
          height,
          minHeight,
          width: "100%",
        }}
      />
    </div>
  );
}
